// stripExif — removes ALL EXIF metadata from JPEG uploads.
//
// Casavio Design Principle #12 (Photo metadata): no Casavio photo, past
// or future, retains location coordinates or device fingerprints. This
// helper is the primary mechanism on the upload path; the
// enumerate-room-items Edge Function adds detection-only logging as a
// belt-and-suspenders safety net.
//
// Scope: JPEG only. Non-JPEG inputs pass through unchanged — server-side
// detection catches anything that slips through. HEIC inputs are
// converted to JPEG upstream (convertHeicIfNeeded), so by the time
// stripExif runs they're already plain JPEG and piexifjs can handle them.
//
// Behavior: if the file is JPEG, runs through piexif.remove() which
// returns a cleaned dataURL. We then rebuild a File object preserving
// name and type so call sites don't need to re-fix their metadata. If
// piexifjs throws (no EXIF block, malformed file, anything weird), we
// return the original file — the server-side detection in v18+ will
// log a warning if real EXIF actually slipped through.

import piexif from 'piexifjs'

const JPEG_MIME_TYPES = new Set(['image/jpeg', 'image/jpg'])

export async function stripExif(file) {
  if (!file || !file.type) return file
  if (!JPEG_MIME_TYPES.has(file.type.toLowerCase())) return file
  try {
    const dataUrl = await blobToDataUrl(file)
    const cleaned = piexif.remove(dataUrl)
    const cleanedBlob = await dataUrlToBlob(cleaned)
    return new File([cleanedBlob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    })
  } catch (err) {
    // piexifjs throws if no EXIF block exists or the file is malformed.
    // Either case is safe to pass through — the server-side detector
    // in enumerate-room-items v18 logs anything real.
    console.warn('[stripExif] Could not strip EXIF, passing through:', err?.message || err)
    return file
  }
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error('FileReader error'))
    reader.readAsDataURL(blob)
  })
}

async function dataUrlToBlob(dataUrl) {
  const res = await fetch(dataUrl)
  return res.blob()
}
